// Grouping utility functions for table
// Builds hierarchical grouped rows with subtotals and grand totals

import type { TableRow, TableColumn, GroupConfig, TableCell, TotalsRowConfig } from '@/app/types/table';
import { calculateAllAggregations } from './aggregationUtils';

/**
 * Build grouped rows from base data rows
 */
export function buildGroupedRows(
  baseRows: TableRow[],
  groupConfig: GroupConfig,
  columns: TableColumn[]
): TableRow[] {
  if (!groupConfig.enabled || groupConfig.columnIds.length === 0) {
    return baseRows;
  }

  // 🔥 FIX: Ensure collapsedGroups is a Set (handle deserialization issues)
  if (!(groupConfig.collapsedGroups instanceof Set)) {
    groupConfig.collapsedGroups = new Set(
      Array.isArray(groupConfig.collapsedGroups) ? groupConfig.collapsedGroups : []
    );
  }

  // Filter out non-data rows (headers, empty rows)
  const dataRows = baseRows.filter(row => !row.isHeader && !row.isEmpty);

  if (dataRows.length === 0) {
    return baseRows;
  }

  // Build hierarchical groups
  const groupedRows: TableRow[] = [];
  const groups = buildGroupHierarchy(dataRows, groupConfig.columnIds, columns);

  // Render groups recursively
  renderGroups(
    groups,
    groupedRows,
    groupConfig,
    columns,
    0, // level
    '' // parent key
  );

  // Add grand total if enabled
  if (groupConfig.showGrandTotal && dataRows.length > 0) {
    const grandTotalRow = createGrandTotalRow(dataRows, groupConfig, columns);
    groupedRows.push(grandTotalRow);
  }

  return groupedRows;
}

/**
 * Build hierarchical group structure
 */
interface GroupNode {
  value: any;
  rows: TableRow[];
  children?: Map<any, GroupNode>;
}

function buildGroupHierarchy(
  rows: TableRow[],
  groupColumnIds: string[],
  columns: TableColumn[],
  level: number = 0
): Map<any, GroupNode> {
  const groups = new Map<any, GroupNode>();

  if (level >= groupColumnIds.length) {
    return groups;
  }

  const columnId = groupColumnIds[level];

  // Group rows by this column's value
  for (const row of rows) {
    const cellValue = row.data[columnId]?.value ?? '(Empty)';
    
    if (!groups.has(cellValue)) {
      groups.set(cellValue, {
        value: cellValue,
        rows: [],
        children: undefined,
      });
    }

    groups.get(cellValue)!.rows.push(row);
  }

  // Recursively build child groups
  if (level + 1 < groupColumnIds.length) {
    for (const [value, node] of groups.entries()) {
      node.children = buildGroupHierarchy(node.rows, groupColumnIds, columns, level + 1);
    }
  }

  return groups;
}

/**
 * Render groups recursively into flat row list
 */
function renderGroups(
  groups: Map<any, GroupNode>,
  outputRows: TableRow[],
  groupConfig: GroupConfig,
  columns: TableColumn[],
  level: number,
  parentKey: string
) {
  const columnId = groupConfig.columnIds[level];

  // Sort groups by value
  const sortedEntries = Array.from(groups.entries()).sort((a, b) => {
    const aVal = String(a[0]);
    const bVal = String(b[0]);
    return aVal.localeCompare(bVal);
  });

  for (const [value, node] of sortedEntries) {
    const groupKey = parentKey ? `${parentKey}::${value}` : String(value);
    const isCollapsed = groupConfig.collapsedGroups.has(groupKey);

    // Add group header row
    const groupHeaderRow = createGroupHeaderRow(
      value,
      columnId,
      node.rows.length,
      level,
      groupKey,
      isCollapsed,
      columns
    );
    outputRows.push(groupHeaderRow);

    // Skip children if collapsed
    if (isCollapsed) {
      continue;
    }

    // Render children (either sub-groups or data rows)
    if (node.children && node.children.size > 0) {
      // Has sub-groups - render recursively
      renderGroups(node.children, outputRows, groupConfig, columns, level + 1, groupKey);
    } else {
      // Leaf level - add data rows
      for (const dataRow of node.rows) {
        outputRows.push(dataRow);
      }
    }

    // Add subtotal row if enabled
    if (groupConfig.showSubtotals) {
      const subtotalRow = createSubtotalRow(
        node.rows,
        groupConfig,
        columns,
        level,
        groupKey
      );
      outputRows.push(subtotalRow);
    }
  }
}

/**
 * Create a group header row
 */
function createGroupHeaderRow(
  groupValue: any,
  columnId: string,
  childCount: number,
  level: number,
  groupKey: string,
  collapsed: boolean,
  columns: TableColumn[]
): TableRow {
  const data: { [columnId: string]: TableCell } = {};

  // Create cells for all columns
  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    
    if (col.id === columnId) {
      // This is the grouped column - show the group value
      data[col.id] = {
        id: `group-${groupKey}-${col.id}`,
        rowIndex: -1,
        colIndex: i,
        value: groupValue,
        dataType: col.dataType,
      };
    } else {
      // Other columns are empty in group header
      data[col.id] = {
        id: `group-${groupKey}-${col.id}`,
        rowIndex: -1,
        colIndex: i,
        value: '',
        dataType: col.dataType,
      };
    }
  }

  return {
    id: `group-header-${groupKey}`,
    index: -1,
    height: 32,
    isGroupHeader: true,
    groupLevel: level,
    groupKey,
    groupValue,
    groupColumnId: columnId,
    childCount,
    collapsed,
    data,
  };
}

/**
 * Create a subtotal row
 */
function createSubtotalRow(
  rows: TableRow[],
  groupConfig: GroupConfig,
  columns: TableColumn[],
  level: number,
  groupKey: string
): TableRow {
  const aggregations = calculateAllAggregations(rows, groupConfig.aggregations, columns);
  const data: { [columnId: string]: TableCell } = {};

  // Create cells for all columns
  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    const aggValue = aggregations[col.id];
    
    data[col.id] = {
      id: `subtotal-${groupKey}-${col.id}`,
      rowIndex: -1,
      colIndex: i,
      value: aggValue !== undefined ? aggValue : '',
      dataType: col.dataType,
    };
  }

  return {
    id: `subtotal-${groupKey}`,
    index: -1,
    height: 32,
    isSubtotal: true,
    groupLevel: level,
    groupKey,
    aggregations,
    data,
  };
}

/**
 * Create grand total row
 */
function createGrandTotalRow(
  rows: TableRow[],
  groupConfig: GroupConfig,
  columns: TableColumn[]
): TableRow {
  const aggregations = calculateAllAggregations(rows, groupConfig.aggregations, columns);
  const data: { [columnId: string]: TableCell } = {};

  // Create cells for all columns
  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    const aggValue = aggregations[col.id];
    
    data[col.id] = {
      id: `grandtotal-${col.id}`,
      rowIndex: -1,
      colIndex: i,
      value: aggValue !== undefined ? aggValue : '',
      dataType: col.dataType,
    };
  }

  return {
    id: 'grand-total',
    index: -1,
    height: 40,
    isGrandTotal: true,
    aggregations,
    data,
  };
}

/**
 * Toggle group collapsed state
 */
export function toggleGroupCollapse(
  groupConfig: GroupConfig,
  groupKey: string
): GroupConfig {
  // 🔥 FIX: Ensure collapsedGroups is a Set
  const currentCollapsed = groupConfig.collapsedGroups instanceof Set 
    ? groupConfig.collapsedGroups 
    : new Set(Array.isArray(groupConfig.collapsedGroups) ? groupConfig.collapsedGroups : []);
  
  const newCollapsedGroups = new Set(currentCollapsed);
  
  if (newCollapsedGroups.has(groupKey)) {
    newCollapsedGroups.delete(groupKey);
  } else {
    newCollapsedGroups.add(groupKey);
  }

  return {
    ...groupConfig,
    collapsedGroups: newCollapsedGroups,
  };
}

/**
 * Create default group config
 */
export function createDefaultGroupConfig(): GroupConfig {
  return {
    id: `group-${Date.now()}`,
    enabled: false,
    columnIds: [],
    aggregations: {},
    showSubtotals: true,
    showGrandTotal: true,
    collapsedGroups: new Set(),
    sortWithinGroups: false,
  };
}

/**
 * Create default totals row config
 */
export function createDefaultTotalsRowConfig(): TotalsRowConfig {
  return {
    enabled: false,
    aggregations: {},
    label: 'Summe',
  };
}